// src/components/Admin/QuestionnaireTemplateListPage.js
// FINAL VERSION with QUERY FIX - June 29, 2025

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase';
import { collection, query, onSnapshot, orderBy, addDoc, updateDoc, doc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import {
    Box, Paper, Typography, Button, TextField, Select, MenuItem,
    Table, TableContainer, TableHead, TableRow, TableCell, TableBody,
    IconButton, Menu, InputLabel, FormControl,
    Dialog, DialogTitle, DialogContent, Divider, DialogActions, DialogContentText
} from '@mui/material';
import { Add as AddIcon, MoreVert as MoreVertIcon, Warning as WarningIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';

import CreateEditUserAgreementPage from '../../pages/admin/CreateEditUserAgreementPage'; // We will create this file later



const UserAgreementListPage = () => {
    // --- STATE MANAGEMENT ---
    const [agreements, setAgreements] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedAgreement, setSelectedAgreement] = useState(null);
    
    const [modalMode, setModalMode] = useState(null);
    const [agreementToEdit, setAgreementToEdit] = useState(null);

    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // --- DATA FETCHING ---
    useEffect(() => {
        const q = query(collection(db, 'userAgreements'), orderBy('title', 'asc')); // Changed collection and orderBy field
const unsubscribe = onSnapshot(q, (querySnapshot) => {
    const agreementsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        lastUpdated: doc.data().lastUpdated?.toDate() // Assuming this field will exist
    }));
    setAgreements(agreementsData);
    setLoading(false);
}, (err) => {
    console.error("Error fetching user agreements:", err);
    setError("Failed to load user agreements.");
    setLoading(false);
});

        return () => unsubscribe();
    }, []);

    // --- FILTERING LOGIC ---
    const filteredAgreements = useMemo(() => {
    if (!agreements) return [];
    return agreements.filter(agreement => {
        const title = agreement.title || ''; // Use 'title'
        const status = agreement.active ? 'Active' : 'Archived'; // Map 'active' boolean
        
        // Search only by title for now
        const matchesSearch = title.toLowerCase().includes(searchQuery.toLowerCase());
        
        const matchesStatus = statusFilter === 'All' || status === statusFilter;
        return matchesSearch && matchesStatus;
    });
}, [agreements, searchQuery, statusFilter]);


    // --- HANDLERS ---
    const handleMenuOpen = (event, agreement) => {
    setAnchorEl(event.currentTarget);
    setSelectedAgreement(agreement);
};

    const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAgreement(null);
};
    
    const handleOpenModal = (mode, agreement = null) => {
    setModalMode(mode);
    setAgreementToEdit(agreement);
    handleMenuClose();
};

    const handleCloseModal = () => {
    setModalMode(null);
    setAgreementToEdit(null);
};

    const handleSaveAgreement = async (agreementData) => {
        setLoading(true);
        try {
            if (modalMode === 'edit' && agreementToEdit?.id) {
                const agreementRef = doc(db, "userAgreements", agreementToEdit.id);
                await updateDoc(agreementRef, { ...agreementData, lastUpdated: serverTimestamp() });
                toast.success(`Agreement "${agreementData.title}" updated successfully!`);
            } else {
                await addDoc(collection(db, "userAgreements"), { ...agreementData, createdAt: serverTimestamp(), lastUpdated: serverTimestamp() });
                toast.success(`Agreement "${agreementData.title}" created successfully!`);
            }
            handleCloseModal();
        } catch (e) {
            console.error("Error saving document: ", e);
            toast.error("Failed to save agreement. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleDuplicate = async () => {
        if (!selectedAgreement) return;
        setLoading(true);
        const { id, ...originalData } = selectedAgreement;
        const newAgreementData = { ...originalData, title: `Copy of ${originalData.title}`, active: false, createdAt: serverTimestamp(), lastUpdated: serverTimestamp() };
        try {
            await addDoc(collection(db, "userAgreements"), newAgreementData);
            toast.success(`Successfully duplicated "${originalData.title}".`);
        } catch (e) {
            console.error("Error duplicating document: ", e);
            toast.error("Failed to duplicate agreement.");
        } finally {
            setLoading(false);
            handleMenuClose();
        }
    };
    
    // --- UPDATED: Delete handler logic fixed ---
    const handleDelete = () => {
        setAnchorEl(null); // Close the menu, but keep the template selected
        setIsDeleteConfirmOpen(true);
    };

    const handleCancelDelete = () => {
    setIsDeleteConfirmOpen(false);
    setSelectedAgreement(null); // Clear selection on cancel
};

    const confirmDelete = async () => {
        if (!selectedAgreement) return;
        setLoading(true);
        try {
            await deleteDoc(doc(db, "userAgreements", selectedAgreement.id));
            toast.success(`Agreement "${selectedAgreement.title}" was permanently deleted.`);
        } catch (e) {
            console.error("Error deleting document: ", e);
            toast.error("Failed to delete agreement.");
        } finally {
            setLoading(false);
            setIsDeleteConfirmOpen(false); // Close the dialog
            setSelectedAgreement(null); // Clear selection
        }
    };


    // --- RENDER LOGIC ---
    return (
        <Paper sx={{ p: 3 }} elevation={2}>
            {/* ... (rest of the JSX is unchanged) ... */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}><Typography variant="h5">User Agreements</Typography><Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal('create')}>Create New Agreement</Button></Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}><TextField label="Search Agreements" variant="outlined" size="small" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={{ flexGrow: 1, minWidth: '200px' }}/><FormControl size="small" sx={{ minWidth: 150 }}><InputLabel>Status</InputLabel><Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}><MenuItem value="All">All</MenuItem><MenuItem value="Active">Active</MenuItem><MenuItem value="Archived">Archived</MenuItem></Select></FormControl></Box>
            <TableContainer><Table>
                <TableHead>
    <TableRow>
        <TableCell>Agreement Title</TableCell> 
        <TableCell>Status</TableCell>
        <TableCell>Date Created</TableCell>
        <TableCell align="right">Actions</TableCell>
    </TableRow>
</TableHead>
<TableBody>
    {filteredAgreements.map((agreement) => (
        <TableRow key={agreement.id} hover>
            <TableCell sx={{ fontWeight: 'medium' }}>{agreement.title}</TableCell>
            <TableCell>{agreement.active ? 'Active' : 'Archived'}</TableCell>
            <TableCell>{agreement.createdAt ? agreement.createdAt.toDate().toLocaleString() : 'N/A'}</TableCell>
            <TableCell align="right"><IconButton onClick={(e) => handleMenuOpen(e, agreement)}><MoreVertIcon /></IconButton></TableCell>
        </TableRow>
    ))}
</TableBody>
            </Table></TableContainer>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
    <MenuItem onClick={() => handleOpenModal('edit', selectedAgreement)}>View / Edit</MenuItem>
    <MenuItem onClick={handleDuplicate}>Duplicate</MenuItem>
    <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>Delete</MenuItem>
</Menu>
<Dialog open={modalMode === 'create' || modalMode === 'edit'} onClose={handleCloseModal} fullWidth maxWidth="lg">
    <DialogTitle>{modalMode === 'edit' ? 'Edit User Agreement' : 'Create New User Agreement'}</DialogTitle><Divider />
    <DialogContent>
        <CreateEditUserAgreementPage agreement={agreementToEdit} onSave={handleSaveAgreement} onClose={handleCloseModal}/>
    </DialogContent>
</Dialog>
<Dialog open={isDeleteConfirmOpen} onClose={handleCancelDelete}>
    <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <WarningIcon color="error" sx={{ mr: 1 }} />
        Confirm Deletion
    </DialogTitle>
    <DialogContent>
        <DialogContentText>
            Are you sure you want to permanently delete the agreement "{selectedAgreement?.title}"? This action cannot be undone.
        </DialogContentText>
    </DialogContent>
    <DialogActions>
        <Button onClick={handleCancelDelete}>Cancel</Button>
        <Button onClick={confirmDelete} color="error" variant="contained">
            Confirm Delete
        </Button>
    </DialogActions>
</Dialog>
        </Paper>
    );
};

export default UserAgreementListPage;
